import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import AddToWorklistModal from 'worklists/AddToWorklistModal';
import ScanIntoWorklistModal from 'worklists/barcode-scanning/ScanIntoWorklistModal';
import {renderWorklistTypeOptions} from 'worklists/renderWorklistTypeOptions';
import {dispatchAddWorklistItems} from 'worklists/WorklistActions';
import {asWorklistType, WorklistType, worklistTypeDisplayName} from 'worklists/WorklistConstants';

import {WorklistSummaryGraphQL} from '../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';

export interface ScanIntoWorklistButtonProps {
  className?: string;
  worklistSummary?: WorklistSummaryGraphQL;
}
const SCANNABLE_WORKLIST_TYPES = [WorklistType.CONTAINER, WorklistType.PLATE, WorklistType.BIOENTITY];

function renderScanIntoWorklistTooltipButton(
  className: string | undefined,
  worklistSummary: WorklistSummaryGraphQL | undefined,
  launchScanIntoWorklistModal: () => void
): JSX.Element {
  const isDisabled =
    worklistSummary && !SCANNABLE_WORKLIST_TYPES.includes(asWorklistType(worklistSummary.itemType));
  const scanButtonTooltipText = worklistSummary
    ? worklistSummary.itemType === WorklistType.BIOENTITY
      ? 'Scan registry IDs into worklist'
      : 'Scan barcodes into worklist'
    : 'Scan items into a worklist';

  return (
    <Tooltip
      title={isDisabled ? 'Scanning into batch worklists is not supported' : scanButtonTooltipText}
      placement={TooltipPlacement.BOTTOM}
    >
      <Button
        onClick={() => {
          launchScanIntoWorklistModal();
        }}
        className={classNames(className, css(styles.defaultCursor))}
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={ButtonSize.SMALL}
        icon="barcode"
        disabled={isDisabled}
      />
    </Tooltip>
  );
}

/**
 * Button that gets worklist items from the ScanIntoWorklistModal, and adds them to a worklist that the user
 * selects from a modal.
 */
export default function ScanIntoWorklistButton(props: ScanIntoWorklistButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const summary = props.worklistSummary;
  const launchScanIntoWorklistModal = async (itemType?: WorklistType): Promise<void> => {
    const worklistType = itemType || asWorklistType(summary!.itemType);
    const ids = await legacyRunModal(ScanIntoWorklistModal, {worklistType});
    if (ids) {
      if (summary) {
        dispatchAddWorklistItems(summary.id, ids, {
          refreshWorklistItemsSearch: true,
          refreshWorklistsSearch: true,
          refreshRecentWorklists: true,
        });
      } else {
        legacyRunModal(AddToWorklistModal, {
          worklistType,
          ids,
          refreshOptions: {
            refreshWorklistItemsSearch: true,
            refreshWorklistsSearch: true,
            refreshRecentWorklists: true,
          },
        });
      }
    }
  };

  if (summary) {
    // Only render basic tooltip and scan button when in the single worklist view
    return renderScanIntoWorklistTooltipButton(
      props.className,
      props.worklistSummary,
      launchScanIntoWorklistModal
    );
  } else {
    // Render dropdown in top-level worklist listing

    const getScanIntoWorklistOptionText = (worklistType: WorklistType): string =>
      `Scan into ${worklistTypeDisplayName(worklistType)} worklist`;
    const openDropdown = (): void => {
      setIsOpen(true);
    };

    return (
      <Dropdown
        className={css(styles.dropdown)}
        isOpen={isOpen}
        onToggle={(newIsOpen) => {
          setIsOpen(newIsOpen);
        }}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.XLARGE}
            onMouseLeave={() => {
              setIsOpen(false);
            }}
            dropLeft={true}
          >
            {renderWorklistTypeOptions({
              onSelectWorklistType: launchScanIntoWorklistModal,
              getOptionText: getScanIntoWorklistOptionText,
              availableWorklistTypes: SCANNABLE_WORKLIST_TYPES,
            })}
          </Menu>
        }
      >
        {renderScanIntoWorklistTooltipButton(props.className, props.worklistSummary, openDropdown)}
      </Dropdown>
    );
  }
}

const styles = StyleSheet.create({
  defaultCursor: {
    ':hover': {
      cursor: 'default',
    },
  },
  dropdown: {
    marginTop: 1,
  },
});
